import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button, Table, Pagination, Modal, Form, Dropdown } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getToken } from 'actions';
import { NotifyModal } from 'components';
import ApiDataEngine from 'services/ApiDataEngine';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const DOWNLOAD_CSV_PATH = '/measurements-nir/v1/download-all.csv';
const DOWNLOAD_JSON_PATH = '/measurements-nir/v1/download-all';
const DOWNLOAD_EXPORT_PATH = '/measurements-nir/v1/download-all/export';

const Results = ({
  user,
  data,
  currentTheme,
  downloadsDisabled = false,
  disableDetailsClick = false,
}) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(null);
  const [orderDirection, setOrderDirection] = useState('asc');

  const [jsonModalShow, setJsonModalShow] = useState(false);
  const [jsonModalSerial, setJsonModalSerial] = useState('');
  const [jsonExportMode, setJsonExportMode] = useState('all');
  const [jsonDateFrom, setJsonDateFrom] = useState('');
  const [jsonDateTo, setJsonDateTo] = useState('');

  const [exportEmailLoading, setExportEmailLoading] = useState(false);
  const [notifyModalShow, setNotifyModalShow] = useState(false);
  const [notifyType, setNotifyType] = useState({ color: '', status: '' });

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  const handleSort = useCallback((column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0);
  }, [orderBy, orderDirection]);

  const getSortIcon = (column) => {
    if (orderBy !== column) return <FaSort className="ms-1" />;
    return orderDirection === 'asc' ?
      <FaSortUp className="ms-1" /> :
      <FaSortDown className="ms-1" />;
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      // Lógica de ordenação específica para cada coluna
      let valueA, valueB;

      switch (orderBy) {
        case 'serialnumber':
          valueA = a.serialnumber || '';
          valueB = b.serialnumber || '';
          break;
        case 'totalMedidas':
          valueA = Number(a.totalMedidas || 0);
          valueB = Number(b.totalMedidas || 0);
          break;
        case 'nameEquipament':
          valueA = a.nameEquipament || '';
          valueB = b.nameEquipament || '';
          break;
        case 'totalMedidasClassifications':
          valueA = Number(a.totalMedidasClassifications ?? 0);
          valueB = Number(b.totalMedidasClassifications ?? 0);
          break;
        case 'totalMedidasLaboratory':
          valueA = Number(a.totalMedidasLaboratory ?? 0);
          valueB = Number(b.totalMedidasLaboratory ?? 0);
          break;
        case 'ultimaMedidaEm':
          valueA = new Date(a.ultimaMedidaEm || 0).getTime();
          valueB = new Date(b.ultimaMedidaEm || 0).getTime();
          break;
        case 'ultimoTsEquipamento':
          valueA = a.ultimoTsEquipamento || '';
          valueB = b.ultimoTsEquipamento || '';
          break;
        case 'daystoactivate':
          valueA = parseInt(a.daystoactivate, 16);
          valueB = parseInt(b.daystoactivate, 16);
          break;
        case 'mode':
          valueA = a.mode || '';
          valueB = b.mode || '';
          break;
        case 'plan':
          valueA = a.plan || '';
          valueB = b.plan || '';
          break;
        case 'versaosw':
          valueA = a.versaosw || '';
          valueB = b.versaosw || '';
          break;
        case 'wf':
          valueA = a.wf === 'OK' ? 1 : 0;
          valueB = b.wf === 'OK' ? 1 : 0;
          break;
        case 'calph':
          valueA = a.calph === 'OK' ? 1 : 0;
          valueB = b.calph === 'OK' ? 1 : 0;
          break;
        case 'dcal':
          valueA = new Date(a.dcal || 0).getTime();
          valueB = new Date(b.dcal || 0).getTime();
          break;
        case 'isentkey':
          valueA = a.isentkey === true ? 1 : 0;
          valueB = b.isentkey === true ? 1 : 0;
          break;
        default:
          valueA = a[orderBy] || '';
          valueB = b[orderBy] || '';
      }

      if (valueA < valueB) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, orderBy, orderDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleRowClick = (serialnumber) => {
    history.push(`/nir/${serialnumber}`);
  };

  // Mesma lógica de NirDetail (formatCreatedAtHeader)
  const formatDate = (dateValue) => {
    if (!dateValue) return '--';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return String(dateValue).replace(/Z$/, '');
    }
    return date.toLocaleString('pt-BR');
  };

  const formatCollectionTimestamp = (tsValue) => {
    if (!tsValue) return '--';
    const ts = String(tsValue);
    const match = ts.match(/^(\d{2})(\d{2})(\d{4})_(\d{2})(\d{2})(\d{2})$/);
    if (!match) return ts;

    const [, day, month, year, hour, minute, second] = match;
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  };

  const formatCreatedAtHeader = (value) => {
    const tsRaw = value;
    const formattedTs = formatCollectionTimestamp(tsRaw);
    if (tsRaw && formattedTs !== String(tsRaw)) return formattedTs;
    return formatDate(value);
  };

  const openDownloadJsonModal = (serialnumber) => {
    setJsonModalSerial(serialnumber);
    setJsonExportMode('all');
    setJsonDateFrom('');
    setJsonDateTo('');
    setJsonModalShow(true);
  };

  const buildDownloadJsonUrl = () => {
    const token = getToken();
    let url = `${process.env.REACT_APP_API_DATA_ENGINE}/measurements-nir/v1/download/${jsonModalSerial}?token=${token}`;
    if (jsonExportMode === 'range' && jsonDateFrom && jsonDateTo) {
      url += `&dateFrom=${encodeURIComponent(jsonDateFrom)}&dateTo=${encodeURIComponent(jsonDateTo)}`;
    }
    return url;
  };

  const handleConfirmDownloadJson = () => {
    if (jsonExportMode === 'range') {
      if (!jsonDateFrom || !jsonDateTo) {
        return;
      }
      const from = new Date(`${jsonDateFrom}T00:00:00`);
      const to = new Date(`${jsonDateTo}T23:59:59.999`);
      if (from > to) {
        return;
      }
    }
    window.open(buildDownloadJsonUrl(), '_blank');
    setJsonModalShow(false);
  };

  const jsonRangeInvalid =
    jsonExportMode === 'range' &&
    (!jsonDateFrom || !jsonDateTo ||
      new Date(`${jsonDateFrom}T00:00:00`) > new Date(`${jsonDateTo}T23:59:59.999`));

  const isDark = currentTheme === 'dark';

  const handleDownloadAll = (format) => {
    const base = process.env.REACT_APP_API_DATA_ENGINE || '';
    const path = format === 'csv' ? DOWNLOAD_CSV_PATH : DOWNLOAD_JSON_PATH;
    const url = `${base}${path}?token=${getToken()}`;
    window.open(url, '_blank');
  };

  const showNotify = (color, status) => {
    setNotifyType({ color, status });
    setNotifyModalShow(true);
  };

  const getExportEmailErrorMessage = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail?.message) {
      const retryMinutes = detail?.error?.details?.retry_after_minutes;
      if (detail.status === 'rate_limit_exceeded' && retryMinutes != null) {
        const minutes = Math.ceil(retryMinutes);
        return `${detail.message} Tente novamente em aproximadamente ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
      }
      return detail.message;
    }
    return (
      error?.response?.data?.message ||
      (typeof error?.response?.data?.error === 'string' ? error.response.data.error : null) ||
      'Falha ao solicitar exportação por e-mail.'
    );
  };

  const handleExportByEmail = async () => {
    const email = user?.email?.trim();
    if (!email) {
      showNotify('#dc3545', 'E-mail do usuário não encontrado.');
      return;
    }

    setExportEmailLoading(true);
    try {
      const { data, status } = await ApiDataEngine.post(
        DOWNLOAD_EXPORT_PATH,
        { email },
        { validateStatus: (s) => s === 202 }
      );

      if (status === 202 && data?.success !== false) {
        showNotify(
          '#198754',
          data?.message || 'Exportação iniciada. Você será notificado por e-mail quando concluir.'
        );
        return;
      }

      showNotify('#dc3545', data?.message || 'Falha ao solicitar exportação por e-mail.');
    } catch (error) {
      showNotify('#dc3545', getExportEmailErrorMessage(error));
    } finally {
      setExportEmailLoading(false);
    }
  };

  return (
    <div>
      <NotifyModal
        show={notifyModalShow}
        type={notifyType}
        onHide={() => setNotifyModalShow(false)}
      />
      <Modal
        show={jsonModalShow}
        onHide={() => setJsonModalShow(false)}
        centered
        contentClassName={isDark ? 'bg-dark text-light border-secondary' : ''}
      >
        <Modal.Header closeButton className={isDark ? 'border-secondary' : ''}>
          <Modal.Title>Download JSON</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Escolha o escopo do arquivo:</p>
          <Form>
            <Form.Check
              className="mb-2"
              type="radio"
              name="jsonExportMode"
              id="json-export-all"
              label="Todos os dados"
              checked={jsonExportMode === 'all'}
              onChange={() => setJsonExportMode('all')}
            />
            <Form.Check
              className="mb-3"
              type="radio"
              name="jsonExportMode"
              id="json-export-range"
              label="Filtrar por período (data inicial e final)"
              checked={jsonExportMode === 'range'}
              onChange={() => setJsonExportMode('range')}
            />
            {jsonExportMode === 'range' && (
              <div className="d-flex flex-wrap gap-3 align-items-end">
                <Form.Group controlId="json-date-from">
                  <Form.Label>Data inicial</Form.Label>
                  <Form.Control
                    type="date"
                    value={jsonDateFrom}
                    onChange={(e) => setJsonDateFrom(e.target.value)}
                    className={isDark ? 'bg-dark text-light border-secondary' : ''}
                  />
                </Form.Group>
                <Form.Group controlId="json-date-to">
                  <Form.Label>Data final</Form.Label>
                  <Form.Control
                    type="date"
                    value={jsonDateTo}
                    onChange={(e) => setJsonDateTo(e.target.value)}
                    className={isDark ? 'bg-dark text-light border-secondary' : ''}
                  />
                </Form.Group>
              </div>
            )}
            {jsonExportMode === 'range' && jsonRangeInvalid && (jsonDateFrom || jsonDateTo) && (
              <p className="text-danger small mt-2 mb-0">
                Informe data inicial e final válidas (inicial não pode ser depois da final).
              </p>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className={isDark ? 'border-secondary' : ''}>
          <Button variant={isDark ? 'outline-light' : 'secondary'} onClick={() => setJsonModalShow(false)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmDownloadJson}
            disabled={downloadsDisabled || (jsonExportMode === 'range' && jsonRangeInvalid)}
          >
            Baixar
          </Button>
        </Modal.Footer>
      </Modal>

      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={
            currentTheme === 'dark'
              ? 'bg-dark text-light border-secondary d-flex flex-wrap justify-content-between align-items-center gap-2'
              : 'bg-light text-dark d-flex flex-wrap justify-content-between align-items-center gap-2'
          }
        >
          <span>Equipamentos</span>
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              variant={currentTheme === 'dark' ? 'outline-light' : 'outline-success'}
              id="dropdown-download-all"
              disabled={downloadsDisabled || !user?.permission?.nir?.write}
            >
              Download
            </Dropdown.Toggle>

            <Dropdown.Menu variant={currentTheme === 'dark' ? 'dark' : 'light'}>
              <Dropdown.Item disabled={downloadsDisabled} onClick={() => handleDownloadAll('csv')}>CSV</Dropdown.Item>
              <Dropdown.Item disabled={downloadsDisabled} onClick={() => handleDownloadAll('json')}>JSON</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={handleExportByEmail}
                disabled={downloadsDisabled || exportEmailLoading}
              >
                {exportEmailLoading ? 'Solicitando...' : 'Exportar por e-mail (background)'}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Card.Header>


        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th className='text-center' onClick={() => handleSort('serialnumber')} style={{ cursor: 'pointer' }}>
                  SERIAL {getSortIcon('serialnumber')}
                </th>
                <th className='text-center' onClick={() => handleSort('nameEquipament')} style={{ cursor: 'pointer' }}>
                  EQUIPAMENTO {getSortIcon('nameEquipament')}
                </th>
                <th className='text-center' onClick={() => handleSort('totalMedidas')} style={{ cursor: 'pointer' }}>
                  TOTAL MEDIDAS {getSortIcon('totalMedidas')}
                </th>
                <th className='text-center' onClick={() => handleSort('totalMedidasClassifications')} style={{ cursor: 'pointer' }}>
                  CLASSIFICAÇÃO {getSortIcon('totalMedidasClassifications')}
                </th>
                <th className='text-center' onClick={() => handleSort('totalMedidasLaboratory')} style={{ cursor: 'pointer' }}>
                  LABORATÓRIO {getSortIcon('totalMedidasLaboratory')}
                </th>
                <th className='text-center' onClick={() => handleSort('ultimaMedidaEm')} style={{ cursor: 'pointer' }}>
                  ÚLTIMA MEDIDA {getSortIcon('ultimaMedidaEm')}
                </th>
                <th className='text-center'>AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr
                  key={item.serialnumber}
                  className={disableDetailsClick ? undefined : 'clickable-row'}
                  onClick={
                    disableDetailsClick
                      ? undefined
                      : () => user?.permission?.nir?.read && handleRowClick(item.serialnumber)
                  }
                  style={{ cursor: disableDetailsClick || !user?.permission?.nir?.read ? 'default' : 'pointer' }}
                >
                  <td className='text-center pt-2'>{item.serialnumber || '--'}</td>
                  <td className='text-center pt-2'>{item.nameEquipament || '--'}</td>
                  <td className='text-center pt-2'>{item.totalMedidas ?? 0}</td>
                  <td className='text-center pt-2'>{item.totalMedidasClassifications ?? 0}</td>
                  <td className='text-center pt-2'>{item.totalMedidasLaboratory ?? 0}</td>
                  <td className='text-center pt-2'>{formatCreatedAtHeader(item.ultimaMedidaEm)}</td>
                  <td className='text-center'>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDownloadJsonModal(item.serialnumber);
                      }}
                      disabled={downloadsDisabled || !user?.permission?.nir?.write}
                    >
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"

            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"

            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
